import { ButtonHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', fullWidth, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center rounded-lg font-medium transition-all',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100',
          {
            // Variants
            'bg-accent text-secondary-foreground hover:bg-accent-600 focus:ring-accent-800 shadow-md':
              variant === 'primary',
            'bg-primary text-secondary-foreground hover:bg-primary-300 focus:ring-primary-800 shadow-md':
              variant === 'secondary',
            'border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-400':
              variant === 'outline',
            'text-secondary-foreground hover:bg-secondary-100 focus:ring-secondary-400':
              variant === 'ghost',

            // Sizes
            'px-4 py-2 text-sm': size === 'sm',
            'px-6 py-3 text-base': size === 'md',
            'px-8 py-4 text-lg': size === 'lg',

            // Full width
            'w-full': fullWidth,
          },
          className
        )}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
